// lib/cockpitPlace.ts — H2 (COCKPIT-HOME-COLUMNS-SPEC.md §H2, "Keeping
// your place"): "the last route and scroll offset of each column's screen
// are kept in sessionStorage (`cockpit.place.<columnId>`, try/catch,
// JSON)". One shared envelope so every column's own screen (Mail today;
// the board itself under columnId 'home') reads and writes the SAME
// convention rather than each re-inventing its own key and error handling
// — same precedent as CockpitTitleSwitcher.ts's own `readUsage`/`bumpUsage`
// (there: localStorage, most-used-first; here: sessionStorage, a place to
// come back to — each degrades the same way: a blocked/missing store is
// not kept, never a thrown error the screen has to handle itself).
//
// What a screen keeps in the blob is its own business — a route, a scroll
// offset, a selected thread id, which account/label was open — this file
// only owns the read/write/JSON/try-catch envelope around it.

const KEY_PREFIX = 'cockpit.place.';

export function readCockpitPlace<T>(columnId: string): T | null {
  try {
    const raw = window.sessionStorage.getItem(`${KEY_PREFIX}${columnId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as T) : null;
  } catch {
    // Private browsing, a blocked store, a corrupt value — the column
    // simply can't remember where it was; it still renders its own
    // default, never a thrown error reaching the screen.
    return null;
  }
}

export function writeCockpitPlace(columnId: string, place: Record<string, unknown>): void {
  try {
    window.sessionStorage.setItem(`${KEY_PREFIX}${columnId}`, JSON.stringify(place));
  } catch {
    // Quota exceeded, a blocked store — nothing to do; the place is
    // simply not kept this time, the column otherwise unaffected.
  }
}
